/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { cn } from '@/lib/utils';
import {
  buttonId,
  canExpand,
  descriptionId,
  FormContextType,
  getTemplate,
  getUiOptions,
  ObjectFieldTemplateProps,
  RJSFSchema,
  StrictRJSFSchema,
  titleId,
} from '@rjsf/utils';

function depthFromId(id: string): number {
  if (!id) return 0;
  // remove leading "root" (exact match or prefix)
  const norm = id.startsWith('root') ? id.slice('root'.length) : id;
  if (!norm) return 0;
  return norm.split('_').filter(Boolean).length;
}

const INDENT_CLASSES = [
  '',
  'pl-8 border-l border-border/50',
  'pl-16 border-l border-border/50',
  'pl-24 border-l border-border/50',
  'pl-32 border-l border-border/50',
  'pl-64 border-l border-border/50',
];
function indentClass(depth: number) {
  return INDENT_CLASSES[Math.min(depth, INDENT_CLASSES.length - 1)];
}

/** The `ObjectFieldTemplate` is the template to use to render all the inner properties of an object along with the
 * title and description if available. If the object is expandable, then an `AddButton` is also rendered after all
 * the properties.
 *
 * @param props - The `ObjectFieldTemplateProps` for this component
 */
function ShObjectFieldTemplateImpl<
  T = any,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends FormContextType = any,
>({
  description,
  title,
  properties,
  required,
  uiSchema,
  idSchema,
  schema,
  formData,
  onAddClick,
  disabled,
  readonly,
  registry,
  startIndentLevel,
  layoutClassNames,
}: ObjectFieldTemplateProps<T, S, F> & {
  startIndentLevel?: number;
  // Define layout class names for different indent levels
  layoutClassNames?: {
    [key: number]: string;
  };
}) {
  const uiOptions = getUiOptions<T, S, F>(uiSchema);
  const TitleFieldTemplate = getTemplate<'TitleFieldTemplate', T, S, F>(
    'TitleFieldTemplate',
    registry,
    uiOptions,
  );
  const DescriptionFieldTemplate = getTemplate<
    'DescriptionFieldTemplate',
    T,
    S,
    F
  >('DescriptionFieldTemplate', registry, uiOptions);

  // Button templates are not overridden in the uiSchema
  const {
    ButtonTemplates: { AddButton },
  } = registry.templates;

  // Indent the object based on its depth in the schema
  const depth = depthFromId(idSchema.$id) - (startIndentLevel || 0);

  return (
    <>
      {title && (
        <TitleFieldTemplate
          id={titleId<T>(idSchema)}
          title={title}
          required={required}
          schema={schema}
          uiSchema={uiSchema}
          registry={registry}
        />
      )}
      {description && (
        <DescriptionFieldTemplate
          id={descriptionId<T>(idSchema)}
          description={description}
          schema={schema}
          uiSchema={uiSchema}
          registry={registry}
        />
      )}
      <div
        id={idSchema.$id}
        data-rjsf-depth={depth}
        className={cn('w-full space-y-4', indentClass(depth))}
      >
        <div
          className={cn(
            'flex flex-col gap-6',
            layoutClassNames ? layoutClassNames[depth] : '',
          )}
        >
          {properties.map((element: any, index: number) => (
            <div
              key={index}
              className={`${element.hidden ? 'hidden' : ''} flex`}
            >
              <div className="w-full"> {element.content}</div>
            </div>
          ))}
          {canExpand(schema, uiSchema, formData) ? (
            <AddButton
              id={buttonId<T>(idSchema, 'add')}
              onClick={onAddClick(schema)}
              disabled={disabled || readonly}
              className="rjsf-object-property-expand"
              uiSchema={uiSchema}
              registry={registry}
            />
          ) : null}
        </div>
      </div>
    </>
  );
}

export function ShObjectFieldTemplate<
  T = any,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends FormContextType = any,
>(options: {
  layoutClassNames?: {
    [key: number]: string;
  };
  startIndentLevel?: number;
}): React.FC<ObjectFieldTemplateProps<T, S, F>> {
  return (props: ObjectFieldTemplateProps<T, S, F>) =>
    ShObjectFieldTemplateImpl<T, S, F>({ ...options, ...props });
}
